import { html, nothing } from "lit";
import { t } from "../i18n.ts";
import type { TerminalState } from "../controllers/terminal.ts";
import { spawnPtySession, writePtyInput, resizePty } from "../controllers/terminal.ts";

export type TerminalProps = {
  connected: boolean;
  input: string;
  history: unknown[];
  running: boolean;
  state: TerminalState;
  onInputChange: (value: string) => void;
  onExec: () => void;
  onClear: () => void;
};

// Persistent xterm state (survives re-renders)
let xtermInstance: import("@xterm/xterm").Terminal | null = null;
let fitAddon: import("@xterm/addon-fit").FitAddon | null = null;
let resizeObserver: ResizeObserver | null = null;
let currentSessionId: string | null = null;
let lastState: TerminalState | null = null;

// Track the persistent container element (created once, never replaced by Lit)
let persistentContainer: HTMLDivElement | null = null;
let xtermOpened = false;
let connectingPty = false;

function getQuickCommands() {
  return [
    { label: t("terminal.status"), cmd: "nabe channels status --probe" },
    { label: t("terminal.config"), cmd: "cat ~/.nabe/nabe.json" },
    { label: t("terminal.disk"), cmd: "df -h /" },
    {
      label: t("terminal.memory"),
      cmd: "free -h 2>/dev/null || vm_stat 2>/dev/null || echo 'N/A'",
    },
    { label: t("terminal.processes"), cmd: "ps aux | head -20" },
    { label: t("terminal.network"), cmd: "ip addr 2>/dev/null || ifconfig 2>/dev/null | head -30" },
    { label: t("terminal.uptime"), cmd: "uptime" },
    { label: t("terminal.logs_btn"), cmd: "tail -50 /tmp/nabe/nabe-$(date +%Y-%m-%d).log" },
  ];
}

let xtermCssLoaded = false;

function loadXtermCss() {
  if (xtermCssLoaded) return;
  xtermCssLoaded = true;
  import("@xterm/xterm/css/xterm.css");
}

async function createXtermInstance() {
  loadXtermCss();

  const [{ Terminal }, { FitAddon }, { WebLinksAddon }] = await Promise.all([
    import("@xterm/xterm"),
    import("@xterm/addon-fit"),
    import("@xterm/addon-web-links"),
  ]);

  const term = new Terminal({
    cursorBlink: true,
    cursorStyle: "bar",
    fontSize: 14,
    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", "Cascadia Code", "Consolas", monospace',
    theme: {
      background: "#1d1f21",
      foreground: "#c5c8c6",
      cursor: "#b5bd68",
      cursorAccent: "#1d1f21",
      selectionBackground: "rgba(108, 140, 255, 0.3)",
      black: "#1d1f21",
      red: "#cc6666",
      green: "#b5bd68",
      yellow: "#f0c674",
      blue: "#81a2be",
      magenta: "#b294bb",
      cyan: "#8abeb7",
      white: "#c5c8c6",
      brightBlack: "#969896",
      brightRed: "#cc6666",
      brightGreen: "#b5bd68",
      brightYellow: "#f0c674",
      brightBlue: "#81a2be",
      brightMagenta: "#b294bb",
      brightCyan: "#8abeb7",
      brightWhite: "#ffffff",
    },
    allowProposedApi: true,
    scrollback: 10000,
    convertEol: true,
  });

  const fit = new FitAddon();
  fitAddon = fit;
  term.loadAddon(fit);
  term.loadAddon(new WebLinksAddon());

  // Forward keyboard input to PTY
  term.onData((data) => {
    if (lastState?.terminalPtySessionId) {
      void writePtyInput(lastState, data);
    } else if (currentSessionId === null && lastState && !connectingPty) {
      // Session ended — reconnect on any keypress
      void connectPty(lastState, term);
    }
  });

  term.onBinary((data) => {
    if (lastState?.terminalPtySessionId) {
      void writePtyInput(lastState, data);
    }
  });

  xtermInstance = term;
  return term;
}

function ensurePersistentContainer(): HTMLDivElement {
  if (persistentContainer) return persistentContainer;

  const div = document.createElement("div");
  div.style.width = "100%";
  div.style.height = "100%";
  persistentContainer = div;
  return div;
}

async function mountXtermInto(host: HTMLElement) {
  const container = ensurePersistentContainer();

  // Append the persistent container to the host if not already there
  if (container.parentElement !== host) {
    host.innerHTML = "";
    host.appendChild(container);
  }

  if (xtermOpened) {
    // Already opened, just refit
    try {
      fitAddon?.fit();
    } catch {
      // ignore
    }
    return;
  }

  if (!xtermInstance) {
    await createXtermInstance();
  }
  if (!xtermInstance) return;

  xtermInstance.open(container);
  xtermOpened = true;

  // Wait for layout to settle before fitting
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  try {
    fitAddon?.fit();
  } catch {
    // ignore
  }

  // Resize observer on the host element
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  resizeObserver = new ResizeObserver(() => {
    try {
      fitAddon?.fit();
      if (lastState?.terminalPtySessionId && fitAddon) {
        const dims = fitAddon.proposeDimensions();
        if (dims) {
          void resizePty(lastState, dims.cols, dims.rows);
        }
      }
    } catch {
      // ignore
    }
  });
  resizeObserver.observe(host);
}

async function connectPty(state: TerminalState, term: import("@xterm/xterm").Terminal) {
  if (currentSessionId || connectingPty) return;
  connectingPty = true;

  term.writeln("\x1b[2m--- Connecting to gateway terminal... ---\x1b[0m");

  try {
    const sessionId = await spawnPtySession(
      state,
      (data) => {
        term.write(data);
      },
      (_exitCode, _signal) => {
        term.writeln(
          "\r\n\x1b[2m--- Terminal session ended. Press any key to reconnect. ---\x1b[0m",
        );
        currentSessionId = null;
      },
    );

    if (sessionId) {
      currentSessionId = sessionId;
      if (fitAddon) {
        const dims = fitAddon.proposeDimensions();
        if (dims) {
          void resizePty(state, dims.cols, dims.rows);
        }
      }
    } else {
      term.writeln("\x1b[31m--- Failed to connect. Check gateway connection. ---\x1b[0m");
    }
  } finally {
    connectingPty = false;
  }
}

function typeCommand(cmd: string) {
  if (xtermInstance && currentSessionId && lastState) {
    void writePtyInput(lastState, cmd + "\n");
  }
}

export function cleanupTerminal() {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (xtermInstance) {
    xtermInstance.dispose();
    xtermInstance = null;
    fitAddon = null;
  }
  persistentContainer = null;
  xtermOpened = false;
  currentSessionId = null;
  lastState = null;
  connectingPty = false;
}

let scheduledMount = false;

export function renderTerminal(props: TerminalProps) {
  lastState = props.state;

  // Schedule xterm mount after Lit renders the DOM (once per animation frame)
  if (!scheduledMount) {
    scheduledMount = true;
    requestAnimationFrame(() => {
      scheduledMount = false;
      const host = document.getElementById("xterm-host");
      if (!host || !props.connected) return;

      void (async () => {
        await mountXtermInto(host);
        // Connect PTY if not connected yet
        if (!currentSessionId && xtermInstance && props.connected && !connectingPty) {
          await connectPty(props.state, xtermInstance);
        }
        xtermInstance?.focus();
      })();
    });
  }

  return html`
    <style>
      @keyframes termBlink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }
      .term-shell {
        background: #1d1f21;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid #373b41;
        box-shadow: 0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03);
        font-family: "JetBrains Mono", "Fira Code", "SF Mono", "Cascadia Code", "Consolas", monospace;
        font-size: 14px;
        line-height: 1.5;
        display: flex;
        flex-direction: column;
        height: calc(100vh - 200px);
        min-height: 400px;
      }
      .term-titlebar {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        background: #282a2e;
        border-bottom: 1px solid #373b41;
        user-select: none;
        flex-shrink: 0;
      }
      .term-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
      }
      .term-dot--red { background: #cc6666; }
      .term-dot--yellow { background: #f0c674; }
      .term-dot--green { background: #b5bd68; }
      .term-titlebar-text {
        flex: 1;
        text-align: center;
        color: #707880;
        font-size: 12px;
        letter-spacing: 0.02em;
      }
      .term-toolbar {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: #282a2e;
        border-bottom: 1px solid #373b41;
        flex-shrink: 0;
        flex-wrap: wrap;
      }
      .term-qbtn {
        padding: 4px 12px;
        background: #373b41;
        border: 1px solid #4a4e57;
        border-radius: 6px;
        color: #81a2be;
        font-size: 12px;
        font-family: inherit;
        cursor: pointer;
        transition: all 0.15s;
        white-space: nowrap;
      }
      .term-qbtn:hover:not(:disabled) {
        background: #4a4e57;
        color: #c5c8c6;
        border-color: #81a2be;
      }
      .term-qbtn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .term-body {
        flex: 1;
        overflow: hidden;
        padding: 4px 8px;
        min-height: 200px;
      }
      .term-body .xterm {
        height: 100%;
      }
      .term-body .xterm-viewport {
        scrollbar-width: thin;
        scrollbar-color: #373b41 transparent;
      }
      .term-body .xterm-viewport::-webkit-scrollbar {
        width: 6px;
      }
      .term-body .xterm-viewport::-webkit-scrollbar-track {
        background: transparent;
      }
      .term-body .xterm-viewport::-webkit-scrollbar-thumb {
        background: #373b41;
        border-radius: 3px;
      }
      .term-status {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 16px;
        background: #1d1f21;
        border-top: 1px solid #373b41;
        font-size: 11px;
        color: #707880;
        flex-shrink: 0;
      }
      .term-status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        flex-shrink: 0;
      }
    </style>

    <div class="term-shell">
      <!-- Title bar (macOS style) -->
      <div class="term-titlebar">
        <div class="term-dot term-dot--red"></div>
        <div class="term-dot term-dot--yellow"></div>
        <div class="term-dot term-dot--green"></div>
        <div class="term-titlebar-text">
          nabe-gateway — ${currentSessionId ? "bash (PTY)" : "connecting..."}
        </div>
        <button
          style="
            background: none;
            border: none;
            color: #707880;
            font-size: 11px;
            cursor: pointer;
            padding: 2px 8px;
            font-family: inherit;
            transition: color 0.15s;
          "
          @click=${() => {
            if (xtermInstance) {
              xtermInstance.clear();
            }
          }}
          @mouseover=${(e: Event) => {
            (e.target as HTMLElement).style.color = "#c5c8c6";
          }}
          @mouseout=${(e: Event) => {
            (e.target as HTMLElement).style.color = "#707880";
          }}
          title="Clear terminal (Ctrl+L)"
        >${t("terminal.clear")}</button>
      </div>

      <!-- Quick command toolbar -->
      <div class="term-toolbar">
        ${getQuickCommands().map(
          (qc) => html`
            <button
              class="term-qbtn"
              ?disabled=${!props.connected || !currentSessionId}
              @click=${() => typeCommand(qc.cmd)}
              title=${qc.cmd}
            >${qc.label}</button>
          `,
        )}
      </div>

      <!-- Terminal body: xterm.js mounts here; NO Lit children allowed (manual DOM only) -->
      <div class="term-body" id="xterm-host"></div>

      <!-- Status bar -->
      <div class="term-status">
        <div class="term-status-dot" style="background: ${props.connected && currentSessionId ? "#b5bd68" : props.connected ? "#f0c674" : "#cc6666"};"></div>
        <span>${props.connected && currentSessionId ? t("topbar.connected") : props.connected ? "Connecting..." : t("topbar.offline")}</span>
        <span style="flex: 1;"></span>
        <span>${currentSessionId ? "PTY" : "—"}</span>
      </div>
    </div>
  `;
}
